/**
 * WrmdForm — mirrors the WRMD public intake form, pre-filled from OCR data.
 *
 * The <form> submits as GET directly to the WRMD embed URL so the rehabber
 * just corrects any errors, solves the Turnstile CAPTCHA, and clicks Submit.
 * No Chrome extension, no intermediate step.
 */

import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Paper from '@mui/material/Paper';

const WRMD_ACTION =
  'https://wrmd.org/embed/public-animal-report-form/' +
  '9D9Nt4EneooYqSF2LIknuWWBCCtd799ujlYvMBqeNcv3xW7B/';

const TURNSTILE_SITEKEY = '0x4AAAAAADP8BfIuxw22_cRX';

const US_STATES = [
  ['US-AK','Alaska'],['US-AL','Alabama'],['US-AR','Arkansas'],
  ['US-AZ','Arizona'],['US-CA','California'],['US-CO','Colorado'],
  ['US-CT','Connecticut'],['US-DC','District of Columbia'],['US-DE','Delaware'],
  ['US-FL','Florida'],['US-GA','Georgia'],['US-HI','Hawaii'],
  ['US-IA','Iowa'],['US-ID','Idaho'],['US-IL','Illinois'],['US-IN','Indiana'],
  ['US-KS','Kansas'],['US-KY','Kentucky'],['US-LA','Louisiana'],
  ['US-MA','Massachusetts'],['US-MD','Maryland'],['US-ME','Maine'],
  ['US-MI','Michigan'],['US-MN','Minnesota'],['US-MO','Missouri'],
  ['US-MS','Mississippi'],['US-MT','Montana'],['US-NC','North Carolina'],
  ['US-ND','North Dakota'],['US-NE','Nebraska'],['US-NH','New Hampshire'],
  ['US-NJ','New Jersey'],['US-NM','New Mexico'],['US-NV','Nevada'],
  ['US-NY','New York'],['US-OH','Ohio'],['US-OK','Oklahoma'],['US-OR','Oregon'],
  ['US-PA','Pennsylvania'],['US-RI','Rhode Island'],['US-SC','South Carolina'],
  ['US-SD','South Dakota'],['US-TN','Tennessee'],['US-TX','Texas'],
  ['US-UT','Utah'],['US-VA','Virginia'],['US-VT','Vermont'],
  ['US-WA','Washington'],['US-WI','Wisconsin'],['US-WV','West Virginia'],
  ['US-WY','Wyoming'],
];

function SectionLabel({ children }) {
  return (
    <Box sx={{ mt: 3, mb: 1.5 }}>
      <Typography
        variant="overline"
        sx={{ color: 'secondary.dark', fontWeight: 700, letterSpacing: '0.12em', fontSize: '0.7rem' }}
      >
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5, borderColor: '#DDD5C0' }} />
    </Box>
  );
}

export default function WrmdForm({ data, warnings = [], onBack }) {
  const [addressSame, setAddressSame] = useState(data?.rescuer_address_same_as_found ?? false);

  // Combined "What happened?" = reason + notes
  const whatHappened = [data?.reasons_for_admission, data?.notes_about_rescue]
    .filter(Boolean).join('. ');

  // Load Cloudflare Turnstile script once
  useEffect(() => {
    if (document.querySelector('script[src*="turnstile"]')) return;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  return (
    <Box sx={{ px: { xs: 2, sm: 0 }, pb: 6 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.dark', mb: 0.5 }}>
        Review &amp; Submit to WRMD
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
        Fields are pre-filled from the intake form photo. Correct anything that looks wrong,
        then complete the CAPTCHA and click <strong>Submit to WRMD</strong>.
      </Typography>

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Some fields may need correction</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </Box>
        </Alert>
      )}

      <Paper
        component="form"
        action={WRMD_ACTION}
        method="get"
        variant="outlined"
        sx={{ borderColor: '#DDD5C0', borderRadius: 3, bgcolor: '#FDFAF4', p: { xs: 2, sm: 3 } }}
      >
        {/* Honeypot — must stay empty */}
        <input type="text" name="hp" value="" readOnly style={{ display: 'none' }} />

        {/* ── About the Animal ──────────────────────────────────── */}
        <SectionLabel>About the Animal</SectionLabel>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="Species (or best guess) *"
              name="common_name"
              defaultValue={data?.common_name ?? ''}
              fullWidth
              required
              placeholder="e.g. Eastern Box Turtle"
              inputProps={{ name: 'common_name' }}
            />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>Number of animals *</InputLabel>
              <Select
                label="Number of animals *"
                defaultValue="1"
                inputProps={{ name: 'animal_count' }}
              >
                {[...Array(20)].map((_, i) => (
                  <MenuItem key={i + 1} value={String(i + 1)}>{i + 1}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* ── Where It Was Found ────────────────────────────────── */}
        <SectionLabel>Where It Was Found</SectionLabel>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="When was it found? *"
              name="found_at"
              type="date"
              defaultValue={data?.found_at ?? ''}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ name: 'found_at' }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Address where found *"
              name="address_found"
              defaultValue={data?.address_found ?? ''}
              fullWidth
              required
              inputProps={{ name: 'address_found' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              label="City *"
              name="city_found"
              defaultValue={data?.city_found ?? ''}
              fullWidth
              required
              inputProps={{ name: 'city_found' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth required>
              <InputLabel>State *</InputLabel>
              <Select
                label="State *"
                defaultValue="US-VA"
                inputProps={{ name: 'subdivision_found' }}
              >
                {US_STATES.map(([val, label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* ── Circumstances ─────────────────────────────────────── */}
        <SectionLabel>Circumstances</SectionLabel>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="What happened? *"
              name="reason_for_admission"
              defaultValue={whatHappened}
              fullWidth
              required
              multiline
              rows={3}
              inputProps={{ name: 'reason_for_admission' }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Did you give it any food, water, or care?"
              name="care_by_rescuer"
              defaultValue={data?.care_by_rescuer ?? ''}
              fullWidth
              multiline
              rows={2}
              placeholder="Leave blank if none"
              inputProps={{ name: 'care_by_rescuer' }}
            />
          </Grid>
        </Grid>

        {/* ── About You (Rescuer) ───────────────────────────────── */}
        <SectionLabel>About the Rescuer</SectionLabel>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="First name *"
              defaultValue={data?.rescuer_first_name ?? ''}
              fullWidth
              required
              inputProps={{ name: 'reporter_first_name' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Last name *"
              defaultValue={data?.rescuer_last_name ?? ''}
              fullWidth
              required
              inputProps={{ name: 'reporter_last_name' }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Phone number *"
              defaultValue={data?.rescuer_phone ?? ''}
              fullWidth
              required
              inputProps={{ name: 'reporter_phone', inputMode: 'tel' }}
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={addressSame}
                  onChange={(e) => setAddressSame(e.target.checked)}
                  name="reporter_address_same_as_found"
                  value="1"
                  color="primary"
                />
              }
              label="My address is the same as where the animal was found"
            />
          </Grid>

          {!addressSame && (
            <>
              <Grid size={12}>
                <TextField
                  label="Your address"
                  defaultValue={data?.rescuer_address ?? ''}
                  fullWidth
                  inputProps={{ name: 'reporter_address' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  label="City"
                  defaultValue={data?.city_found ?? ''}
                  fullWidth
                  inputProps={{ name: 'reporter_city' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    label="State"
                    defaultValue="US-VA"
                    inputProps={{ name: 'reporter_subdivision' }}
                  >
                    {US_STATES.map(([val, label]) => (
                      <MenuItem key={val} value={val}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>Can you bring the animal to us?</InputLabel>
              <Select
                label="Can you bring the animal to us?"
                defaultValue=""
                inputProps={{ name: 'can_pick_up' }}
              >
                <MenuItem value=""><em>— Select —</em></MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
                <MenuItem value="Maybe">Maybe</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* ── CAPTCHA + Submit ──────────────────────────────────── */}
        <Box sx={{ mt: 3 }}>
          <div
            className="cf-turnstile"
            data-sitekey={TURNSTILE_SITEKEY}
            data-theme="light"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            type="button"
            onClick={onBack}
            sx={{ py: 1.5, minWidth: 160 }}
          >
            Upload New Photo
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            type="submit"
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            Submit to WRMD
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
